import { CoachVisibleMessage, DailyInsightCue } from './coachVisibleMessage';
import { noteCoachOutputCommitted } from '../../services/coachSequenceTiming';
import React, { useMemo, useEffect, useRef } from 'react';
import { getAvatarVariantUrl } from '../../utils/avatarVariants';
import CoachAvatar from '../CoachAvatar';
import CoachFeedLoadingSlot from './CoachFeedLoadingSlot';
import {
  isOngoing,
  computeUserStats,
  getActivityDetail,
  timeAgo,
  timeUntil,
} from './DojoPulse';
import PenaltyCallout from './PenaltyCallout';
import { CoachInlineTyping } from './coachInlineTyping';
import { CoachActionCards } from '../CoachActionCard';
import CoachMessageContent from './CoachMessageContent';
import './DojoHub.css';
import { countsTowardDojoDayTotals, dojoActivityRowKey, getDojoMatrixTimingPresentation, isDojoAgendaMissedActivity } from './dojoActivityDayProjection';

/**
 * DojoHub — unified dojo coach + member activity card.
 *
 * Merges the ambient DojoPulse member feed with the DayRecommendationCard
 * so that AI coach advice is presented in the context of the dojo group.
 *
 * Two hub layers:
 *  1. Coach Advice — dojo-branded AI recommendation with typewriter
 *  2. Group Progress Bar — aggregate day stats at a glance
 * Related member status is embedded in each personal activity card by
 * DojoActivityParticipants below.
 */


/* ── Group-level stats computed from all member data ── */

const computeGroupStats = (memberData, dojoActivities) => {
  let totalVolume = 0;
  let complianceSum = 0;
  let complianceCount = 0;
  let workoutsCompleted = 0;
  let workoutsTotal = 0;

  memberData.forEach(({ activities, stats }) => {
    totalVolume += stats.totalVolume;
    if (stats.calorieScore !== null) {
      complianceSum += stats.calorieScore;
      complianceCount++;
    }
    activities.forEach(a => {
      if (a.type === 'workout') {
        workoutsTotal++;
        const sameDayReplacementCompletion = a._dojoReplacementReference?.relativeDays === 0;
        if (countsTowardDojoDayTotals(a)
            && (['complete', 'completed'].includes(a.status) || sameDayReplacementCompletion)) workoutsCompleted++;
      }
    });
  });

  const avgCompliance = complianceCount > 0 ? Math.round(complianceSum / complianceCount) : null;
  const liveCount = dojoActivities.filter(isOngoing).length;

  return { totalVolume, avgCompliance, workoutsCompleted, workoutsTotal, liveCount };
};

/* ── Coach strategy progression panel ── */

const CoachProgressionPanel = ({ progression }) => {
  if (!progression) return null;
  const { weekLabel, detail, percent, status } = progression;
  const isDeterminate = typeof percent === 'number';

  return (
    <div className={`dojo-hub-coach-progression ${status.className}`}>
      <div className="dojo-hub-coach-progression-top">
        <span className="dojo-hub-coach-progression-week">{weekLabel || 'Strategy progress'}</span>
        <span className={`dojo-hub-coach-progression-status ${status.className}`}>
          <span aria-hidden="true">{status.icon}</span>
          {status.label}
        </span>
      </div>
      {detail && <p className="dojo-hub-coach-progression-detail">{detail}</p>}
      {isDeterminate && (
        <div
          className="dojo-hub-coach-progression-track"
          role="progressbar"
          aria-label="Strategy timeline progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-valuetext={`${weekLabel} — ${percent}% of the strategy timeline`}
        >
          <span className="dojo-hub-coach-progression-fill" style={{ width: `${percent}%` }} />
        </div>
      )}
    </div>
  );
};

/* ── Coach Advice Section ── */

const CoachSection = React.memo(({
  recommendation, coachMessage, coachMessageSettled, isToday, isGenerating, dailyPending, isThinking, openRecommendations,
  aiCoachName, aiCoachEmoji, aiCoachImageUrl, dojoName,
  hasAiFeature, openUpgrade, openCoachChat, backendService,
}) => {
  const openMessage = (hasAiFeature && openCoachChat)
    ? () => {
        if (coachMessage?.focusInsightId) {
          openCoachChat({ focusInsightId: coachMessage.focusInsightId });
        } else {
          openCoachChat();
        }
      }
    : openRecommendations;

  // Locked state for non-AI users
  if (isToday && !hasAiFeature) {
    return (
      <div className="dojo-hub-coach locked" onClick={openUpgrade}>
        <div className="dojo-hub-coach-header">
          <div className="dojo-hub-coach-avatar">
            <CoachAvatar imageUrl={aiCoachImageUrl} emoji={aiCoachEmoji || '🥋'} identitySettled alt="Coach" eager />
          </div>
          <div className="dojo-hub-coach-label">
            <span className="dojo-hub-coach-title">{aiCoachName || `${dojoName || 'Dojo'} Coach`}</span>
          </div>
        </div>
        <div className="dojo-hub-coach-text">
          <p className="locked-text">
            <strong>🔒 AI Assistant</strong> — Get personalized training insights and recommendations
          </p>
        </div>
        <span className="upgrade-hint">Upgrade to Pro →</span>
      </div>
    );
  }

  // Feed membership and initial Home reads choose content, not chat admission.
  // Canonical Hub owns its context loading and send guards.
  if (isToday && !coachMessage) {
    return (
      <div className={`dojo-hub-coach ${isGenerating || isThinking ? 'generating' : 'loading-message'}`}>
      <CoachFeedLoadingSlot contextLoading aiCoachName={aiCoachName || `${dojoName || 'Dojo'} Coach`}
        aiCoachEmoji={aiCoachEmoji} aiCoachImageUrl={aiCoachImageUrl}
        hasAiFeature={hasAiFeature} openCoachChat={openCoachChat} dailyPending={isGenerating || dailyPending}>
        {isThinking && (
          <CoachInlineTyping name={aiCoachName} />
        )}
      </CoachFeedLoadingSlot>
      </div>
    );
  }

  if (!coachMessage) return null;
  const displayedRecommendationId = String(recommendation?._id || '');
  const messageInsightId = String(coachMessage.focusInsightId || '');
  const showRecommendationCallout = isToday && displayedRecommendationId && displayedRecommendationId === messageInsightId;
  // The progression panel replaces the strategy status paragraph it was built from
  // (wherever it lands — it's the primary line when there's no general advice, e.g.
  // an activity-less daily insight), so drop it before choosing primary/context text.
  const statusParagraph = coachMessage.progression?.statusSummary || null;
  const visibleParagraphs = coachMessage.paragraphs
    .filter(paragraph => !(statusParagraph && paragraph.trim() === statusParagraph));
  const [primaryParagraph, ...contextParagraphs] = visibleParagraphs;

  return (
    <div className="dojo-hub-coach" onClick={openMessage}>
      {showRecommendationCallout && (
        <PenaltyCallout
          callout={recommendation?.data?.penalty_callout}
          onClick={openMessage}
        />
      )}
      <div className="dojo-hub-coach-header">
        <div className="dojo-hub-coach-avatar">
          <CoachAvatar imageUrl={aiCoachImageUrl} emoji={aiCoachEmoji || '🥋'} identitySettled alt="Coach" eager />
        </div>
        <div className="dojo-hub-coach-label">
          <span className="dojo-hub-coach-title">{aiCoachName || `${dojoName || 'Dojo'} Coach`}</span>
        </div>
      </div>

      {primaryParagraph && (
        <div className="dojo-hub-coach-text daily-insight-ready" ref={element => {
          // Exact identity only: Today proves the awaited insight when it
          // renders that recommendation, bound to its status context.
          if (element && showRecommendationCallout && recommendation?.status_context_id) {
            noteCoachOutputCommitted('readiness_review', String(recommendation.status_context_id), element, 'today_card');
          }
        }}>
          <CoachVisibleMessage as="div" key={`${coachMessage.id}-paragraph-0`} insightId={messageInsightId} text={primaryParagraph}>
            <CoachMessageContent text={primaryParagraph} />
          </CoachVisibleMessage>
        </div>
      )}

      {coachMessage.actions.length > 0 && <CoachActionCards actions={coachMessage.actions} backendService={backendService} />}

      <CoachProgressionPanel progression={coachMessage.progression} />

      {contextParagraphs.length > 0 && (
        <div className="dojo-hub-coach-text dojo-hub-coach-context">
          {contextParagraphs.map((paragraph, index) => (
            <CoachVisibleMessage as="div" key={`${coachMessage.id}-context-${index}`} insightId={messageInsightId} text={paragraph}>
              <CoachMessageContent text={paragraph} />
            </CoachVisibleMessage>
          ))}
        </div>
      )}

      {isToday && (isGenerating || dailyPending) && <DailyInsightCue />}
      {isToday && isThinking && <CoachInlineTyping name={aiCoachName} />}
      {isToday && hasAiFeature && openCoachChat && (
        <div className="dojo-hub-coach-cta">
          <button
            type="button"
            className="dojo-hub-chat-btn"
            onClick={(event) => {
              event.stopPropagation();
              openMessage();
            }}
          >
            💬 Chat with {aiCoachName || 'your coach'} →
          </button>
        </div>
      )}
    </div>
  );
});

/* ── Group Progress Bar ── */

const GroupProgressBar = ({ groupStats, totalMembers, hasOngoing }) => {
  const { totalVolume, avgCompliance, workoutsCompleted, workoutsTotal, liveCount } = groupStats;

  let complianceClass = 'compliance-good';
  if (avgCompliance !== null && avgCompliance < 60) complianceClass = 'compliance-bad';
  else if (avgCompliance !== null && avgCompliance < 80) complianceClass = 'compliance-ok';

  return (
    <div className="dojo-hub-progress">
      {workoutsTotal > 0 && (
        <>
          <div className="dojo-hub-progress-stat">
            <span className="stat-icon">🏋️</span>
            <span className="stat-value">{workoutsCompleted}/{workoutsTotal}</span>
            <span>workouts</span>
          </div>
          <div className="dojo-hub-progress-divider" />
        </>
      )}
      {totalVolume > 0 && (
        <>
          <div className="dojo-hub-progress-stat">
            <span className="stat-value volume">+{totalVolume.toLocaleString()}</span>
            <span>vol</span>
          </div>
          <div className="dojo-hub-progress-divider" />
        </>
      )}
      {avgCompliance !== null && (
        <>
          <div className="dojo-hub-progress-stat">
            <span className={`stat-value ${complianceClass}`}>{avgCompliance}%</span>
            <span>nutrition</span>
          </div>
          <div className="dojo-hub-progress-divider" />
        </>
      )}
      <div className="dojo-hub-progress-stat">
        <span className="stat-value">{totalMembers}</span>
        <span>member{totalMembers !== 1 ? 's' : ''}</span>
        {hasOngoing && (
          <>
            <span> · </span>
            <span className="stat-value live">{liveCount}</span>
            <span>live</span>
          </>
        )}
      </div>
    </div>
  );
};

/* ── Shared activities in the main feed ── */

const MATRIX_TYPE_ORDER = ['workout', 'meal', 'media', 'metric', 'survey', 'other'];
const TERMINAL_NON_PENDING_STATUSES = new Set(['cancelled', 'canceled', 'skipped', 'stopped', 'rest', 'replaced']);
const getParticipantSegmentLabel = ({
  activity,
  completedForDay,
  ongoing,
  missed,
  expired,
  timingClasses,
  isTravelReplacement,
  replacementReference,
  pending,
  rawStatusDetail,
}) => {
  if (completedForDay) return 'Done';
  if (ongoing) return 'Live';
  if (missed) return 'Missed';
  if (expired) return 'Expired';
  if (timingClasses.includes('deferred')) return 'Deferred';
  if (timingClasses.includes('late')) return 'Late';
  if (timingClasses.includes('carryover')) return 'Carryover';
  if (isTravelReplacement) return 'Travel';
  if (replacementReference) return 'Offset';
  if (pending) return 'Pending';
  if (!activity) return 'None';
  return rawStatusDetail
    ? `${rawStatusDetail.charAt(0).toUpperCase()}${rawStatusDetail.slice(1)}`
    : 'Closed';
};

export const DojoActivityParticipants = ({ activity: personalActivity, dojoUsers = [], dojoActivities = [], dojoByDate = {}, currentUserId, onActivityClick, compact = false }) => {
  const members = useMemo(() => {
    const membersById = new Map();
    (dojoUsers || []).forEach(user => {
      const userId = user._id || user.user_id || user.id;
      if (userId) membersById.set(String(userId), user);
    });
    dojoActivities.forEach(activity => {
      const userId = activity.user_id;
      if (userId && !membersById.has(String(userId))) {
        membersById.set(String(userId), { _id: userId, display_name: 'Dojo member' });
      }
    });
    return [...membersById.values()].sort((a, b) => (
      Number(String(b._id || b.user_id || b.id) === String(currentUserId))
        - Number(String(a._id || a.user_id || a.id) === String(currentUserId))
    ));
  }, [currentUserId, dojoActivities, dojoUsers]);
  const rows = useMemo(() => {
    const grouped = new Map();
    const projectedPersonalActivity = personalActivity?._id
      ? dojoActivities.find(candidate => candidate?._id && String(candidate._id) === String(personalActivity._id))
      : null;
    const personalRowKey = dojoActivityRowKey(projectedPersonalActivity || personalActivity);
    dojoActivities.forEach(activity => {
      const key = dojoActivityRowKey(activity);
      if (key !== personalRowKey) return;
      if (!grouped.has(key)) grouped.set(key, { key, type: activity.type, name: activity.name || 'Activity', activities: new Map() });
      grouped.get(key).activities.set(String(activity.user_id), activity);
    });
    // Enrich only this carryover rail, never the daily projection/totals.
    // The bucket and timing keys already use the viewer's timezone. Require
    // stable plan identity: names alone cannot identify a recurring occurrence.
    const anchor = projectedPersonalActivity || personalActivity;
    const timing = anchor?._dojoTiming;
    const entryId = anchor?.source?.activity_plan_entry_id;
    const row = grouped.get(personalRowKey);
    if (row && anchor.type === 'workout' && timing?.isCarryover && entryId) {
      row.priorCompletionLabels = new Map();
      (dojoByDate[timing.scheduledDateKey] || []).forEach(candidate => {
        const candidateTiming = candidate._dojoTiming;
        if (candidate.type !== anchor.type
            || String(candidate.source?.activity_plan_entry_id || '') !== String(entryId)
            || candidateTiming?.scheduledDateKey !== timing.scheduledDateKey
            || !['complete', 'completed'].includes(candidate.status)
            || !candidateTiming.completedDateKey
            || candidateTiming.completedDateKey >= timing.displayDateKey
            || row.activities.has(String(candidate.user_id))) return;
        const days = Math.round((Date.parse(timing.displayDateKey) - Date.parse(candidateTiming.completedDateKey)) / 86400000);
        if (!Number.isFinite(days) || days < 1) return;
        row.activities.set(String(candidate.user_id), candidate);
        row.priorCompletionLabels.set(String(candidate.user_id), `Done ${days === 1 ? 'yesterday' : `${days} days earlier`}`);
      });
    }
    return [...grouped.values()].sort((a, b) => {
      const aType = MATRIX_TYPE_ORDER.indexOf(a.type) < 0 ? 99 : MATRIX_TYPE_ORDER.indexOf(a.type);
      const bType = MATRIX_TYPE_ORDER.indexOf(b.type) < 0 ? 99 : MATRIX_TYPE_ORDER.indexOf(b.type);
      if (aType !== bType) return aType - bType;
      return (a.activities.values().next().value?.source?.plan_order ?? Infinity) - (b.activities.values().next().value?.source?.plan_order ?? Infinity);
    });
  }, [dojoActivities, dojoByDate, personalActivity]);
  const originalsByReplacementId = useMemo(() => {
    const originals = new Map();
    dojoActivities.forEach(activity => {
      if (activity.travel_replacement_id) {
        originals.set(String(activity.travel_replacement_id), activity);
      }
    });
    return originals;
  }, [dojoActivities]);

  // Both sizes resolve the exact same projected occurrence and member states.
  // The header overview is phrasing-only and noninteractive so clicks expand it.
  const Container = compact ? 'span' : 'div';
  const Member = compact ? 'span' : 'button';
  if (!rows.length || !members.length) return null;

  return (
    <Container className={`dojo-inline-participants${compact ? ' is-mini' : ''}`} aria-label={`Dojo activity status for ${personalActivity?.name || 'activity'}`}>
      {rows.map(row => {
        let completedCount = 0;
        const memberPresentations = members.map(user => {
          const userId = String(user._id || user.user_id || user.id);
          const activity = row.activities.get(userId);
          const priorCompletionLabel = row.priorCompletionLabels?.get(userId);
          const completed = activity?.status === 'complete' || activity?.status === 'completed';
          const replacementReference = activity?._dojoReplacementReference;
          const completedForDay = Boolean(priorCompletionLabel) || (countsTowardDojoDayTotals(activity)
            && (completed || replacementReference?.relativeDays === 0));
          if (completedForDay) completedCount += 1;
          const missed = isDojoAgendaMissedActivity(activity);
          const referencedReplacement = replacementReference?.activity;
          const relativeDays = replacementReference?.relativeDays || 0;
          const relativeDayCount = Math.abs(relativeDays);
          const replacementTimingDetail = replacementReference
            ? (relativeDays === 0
              ? 'completed the same day'
              : `completed ${relativeDayCount} day${relativeDayCount === 1 ? '' : 's'} ${relativeDays < 0 ? 'earlier' : 'later'}`)
            : null;
          const expired = activity && !replacementReference && !completed && !missed && activity.due_at && new Date(activity.due_at) < new Date();
          const terminalNonPending = TERMINAL_NON_PENDING_STATUSES.has(String(activity?.status || '').toLowerCase());
          const ongoing = Boolean(activity && !completed && !missed && !expired && !terminalNonPending && isOngoing(activity));
          const time = activity && !missed && !terminalNonPending && (completed || ongoing ? timeAgo(activity.completed_at || activity.created_at) : timeUntil(activity.due_at));
          const timing = activity && !priorCompletionLabel && getDojoMatrixTimingPresentation(activity, { completed, ongoing, time });
          const icon = timing?.icon || (missed || expired ? '✗' : completed ? '✓' : ongoing ? '●' : '—');
          const activityDetail = activity && getActivityDetail(activity);
          const ordinaryDetail = activityDetail && time ? `${activityDetail} · ${time}` : activityDetail || time;
          const rawStatusDetail = String(activity?.status || '').replace(/_/g, ' ');
          const detail = priorCompletionLabel || timing?.detail || (activity && (missed ? 'missed' : ordinaryDetail || (completed ? 'done' : rawStatusDetail || 'pending')));
          const timingClasses = timing?.className || '';
          const isTravelReplacement = activity?.source?.compliance_role === 'travel_replacement';
          const pending = Boolean(activity && !terminalNonPending && !completed && !ongoing && !missed && !expired && !replacementReference && !isTravelReplacement);
          const replacedOriginal = isTravelReplacement
            ? originalsByReplacementId.get(String(activity._id))
            : null;
          const replacedOriginalName = isTravelReplacement
            ? (replacedOriginal?.name || activity.source?.replaces_activity_name || 'original workout')
            : null;
          const isSelf = userId === String(currentUserId);
          const memberLabel = isSelf ? 'You' : (user.display_name || 'Member');
          const marker = !activity
            ? '·'
            : replacementReference
              ? (relativeDays < 0 ? '↙' : relativeDays > 0 ? '↗' : '↔')
              : isTravelReplacement
                ? '↔'
                : icon;
          const statusDetail = !activity
            ? 'Not scheduled'
            : replacementReference
              ? `${referencedReplacement.name || 'Replacement workout'} · ${replacementTimingDetail}`
              : isTravelReplacement
                ? `Travel replacement · replaces ${replacedOriginalName}`
                : detail || (completedForDay ? 'completed' : 'pending');
          const cellLabel = activity
            ? `${memberLabel}: ${row.name}. ${replacementReference ? `Replaced by ${referencedReplacement.name || 'replacement workout'}. ${replacementTimingDetail}` : `${isTravelReplacement ? `Travel replacement. Replaces ${replacedOriginalName}. ` : ''}${detail || (completedForDay ? 'completed' : 'pending')}`}`
            : `${memberLabel}: no matching ${row.name} activity`;
          const clickedActivity = referencedReplacement || activity;
          const segmentLabel = priorCompletionLabel || getParticipantSegmentLabel({ activity, completedForDay, ongoing, missed, expired, timingClasses, isTravelReplacement, replacementReference, pending, rawStatusDetail });
          return { user, activity, clickedActivity, isSelf, memberLabel, marker, segmentLabel, statusDetail, cellLabel, completedForDay, ongoing, pending, missed, expired, isTravelReplacement, replacementReference, timingClasses };
        });

        return (
          <Container className="dojo-inline-participant-row" key={row.key} data-completion={`${completedCount}/${members.length}`}
            role={compact ? 'img' : undefined}
            aria-label={compact ? `Dojo: ${completedCount}/${members.length} complete. ${memberPresentations.map(p => `${p.memberLabel}: ${p.segmentLabel}`).join('; ')}` : undefined}>
            <Container className="dojo-inline-compact-row">
              <Container className="dojo-shared-members" aria-label={compact ? undefined : 'Dojo participants'} aria-hidden={compact || undefined}>
                {memberPresentations.map(presentation => (
                  <Member
                    key={presentation.user._id || presentation.user.user_id || presentation.user.id}
                    type={compact ? undefined : 'button'}
                    className={`dojo-shared-member ${presentation.isSelf ? 'is-self' : ''} ${presentation.completedForDay ? 'completed' : ''} ${presentation.ongoing ? 'ongoing' : ''} ${presentation.pending ? 'pending' : ''} ${presentation.missed ? 'missed' : ''} ${presentation.expired ? 'expired' : ''} ${!presentation.activity ? 'missing' : ''} ${presentation.isTravelReplacement ? 'travel-replacement' : ''} ${presentation.replacementReference ? 'replacement-reference ghost' : ''} ${presentation.timingClasses}`}
                    onClick={compact ? undefined : () => presentation.activity && onActivityClick && onActivityClick(presentation.clickedActivity, presentation.user)}
                    aria-label={`${presentation.cellLabel}. Status: ${presentation.segmentLabel}`}
                    title={`${presentation.memberLabel}: ${presentation.statusDetail}`}
                    disabled={compact ? undefined : !presentation.activity}
                  >
                    <span className="dojo-shared-member-avatar-wrap">
                      {presentation.user.avatar_url
                        ? <img src={getAvatarVariantUrl(presentation.user, 'xs')} alt="" className="dojo-shared-member-avatar" />
                        : <span className="dojo-shared-member-avatar-placeholder">{(presentation.user.display_name || '?')[0].toUpperCase()}</span>}
                      <span className="dojo-shared-member-status" aria-hidden="true">{presentation.marker}</span>
                    </span>
                    <span className="dojo-shared-member-copy" aria-hidden="true">{presentation.segmentLabel}</span>
                  </Member>
                ))}
              </Container>
              {!compact && <span className="dojo-inline-progress">{completedCount}/{members.length} complete</span>}
            </Container>
          </Container>
        );
      })}
    </Container>
  );
};

/* ═══════════════════════════════════════════════════
   DojoHub — main export
   ═══════════════════════════════════════════════════ */

const DojoHub = ({
  // DojoPulse props
  dojoActivities,
  dojoUsers,
  dojoName,
  backendService,
  // DayRecommendationCard props
  recommendation,
  coachMessage,
  coachMessageSettled,
  isToday,
  isGenerating,
  dailyPending,
  isThinking,
  openRecommendations,
  aiCoachName,
  aiCoachEmoji,
  aiCoachImageUrl,
  hasAiFeature,
  openUpgrade,
  openCoachChat,
}) => {
  // Compute member data (reused from DojoPulse logic)
  const memberData = useMemo(() => {
    if (!dojoActivities || dojoActivities.length === 0) return [];

    const userMap = {};
    (dojoUsers || []).forEach(u => { userMap[u._id || u.user_id] = u; });

    const byUser = {};
    dojoActivities.forEach(a => {
      const uid = a.user_id?.toString() || a.user_id;
      if (!byUser[uid]) byUser[uid] = [];
      byUser[uid].push(a);
    });

    const typeOrder = ['workout', 'meal', 'media', 'metric', 'survey', 'other'];
    const sortWithinType = (a, b) => {
      if (a.source?.plan_order !== undefined && b.source?.plan_order !== undefined) {
        return a.source.plan_order - b.source.plan_order;
      }
      const aTime = a.due_at ? new Date(a.due_at).getTime() : Infinity;
      const bTime = b.due_at ? new Date(b.due_at).getTime() : Infinity;
      if (aTime !== bTime) return aTime - bTime;
      const aCreated = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bCreated = b.created_at ? new Date(b.created_at).getTime() : 0;
      return aCreated - bCreated;
    };

    return Object.entries(byUser).map(([userId, acts]) => {
      const user = userMap[userId] || { _id: userId, display_name: 'Dojo member' };
      const sortedActs = [...acts].sort((a, b) => {
        const aIdx = typeOrder.indexOf(a.type) === -1 ? typeOrder.indexOf('other') : typeOrder.indexOf(a.type);
        const bIdx = typeOrder.indexOf(b.type) === -1 ? typeOrder.indexOf('other') : typeOrder.indexOf(b.type);
        if (aIdx !== bIdx) return aIdx - bIdx;
        return sortWithinType(a, b);
      });
      return {
        user,
        activities: sortedActs,
        stats: computeUserStats(sortedActs.filter(countsTowardDojoDayTotals), user),
      };
    }).sort((a, b) => {
      const aOn = a.activities.some(isOngoing) ? 1 : 0;
      const bOn = b.activities.some(isOngoing) ? 1 : 0;
      if (bOn !== aOn) return bOn - aOn;
      return b.activities.length - a.activities.length;
    });
  }, [dojoActivities, dojoUsers]);

  const totalMembers = (dojoUsers || []).length || memberData.length;
  const hasActivities = dojoActivities && dojoActivities.length > 0;
  const hasOngoing = hasActivities && dojoActivities.some(isOngoing);

  const groupStats = useMemo(
    () => hasActivities ? computeGroupStats(memberData, dojoActivities) : null,
    [memberData, dojoActivities, hasActivities]
  );

  // Show coach section even if no member activities (today's daily generation
  // or an activity-triggered review before the first recommendation exists).
  const showCoach = isToday || coachMessage || !coachMessageSettled;

  // Track whether the card has already mounted so fade-in plays only once
  const hasMountedRef = useRef(false);
  useEffect(() => { hasMountedRef.current = true; }, []);

  if (!showCoach && !hasActivities) return null;

  return (
    <div className="dojo-hub-stack">
      <div className={`dojo-hub ${hasOngoing ? 'has-ongoing' : ''} ${isToday ? 'today' : 'historical'} ${!hasMountedRef.current ? 'initial' : ''}`}>
        <div className="dojo-hub-accent" />

        {showCoach && (
          <CoachSection
            recommendation={recommendation}
            coachMessage={coachMessage}
            coachMessageSettled={coachMessageSettled}
            isToday={isToday}
            isGenerating={isGenerating}
            dailyPending={dailyPending}
            isThinking={isThinking}
            openRecommendations={openRecommendations}
            aiCoachName={aiCoachName}
            aiCoachEmoji={aiCoachEmoji}
            aiCoachImageUrl={aiCoachImageUrl}
            dojoName={dojoName}
            hasAiFeature={hasAiFeature}
            openUpgrade={openUpgrade}
            openCoachChat={openCoachChat}
            backendService={backendService}
          />
        )}

        {!isToday && hasActivities && groupStats && (
          <GroupProgressBar
            groupStats={groupStats}
            totalMembers={totalMembers}
            hasOngoing={hasOngoing}
          />
        )}
      </div>
    </div>
  );
};

export default DojoHub;
